/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { AgentExecutionStreamEvent, StreamingExecutionStartResponseData } from '@shared/types/responses/streaming';
import { AgentStreamingExecutionRequestData } from '@shared/types/requests/streaming';
import { SessionExecutionStatusResponseData } from '@shared/types/responses/streaming';
import { authenticatedFetch, createQueryFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import { isAuthDisabled } from '@shared/utils/env';

/**
 * Event handler function type for streaming events
 */
export type StreamEventHandler = (event: AgentExecutionStreamEvent) => void;

/**
 * Error handler function type for streaming errors
 */
export type StreamErrorHandler = (error: Error) => void;

/**
 * Stream completion handler function type
 */
export type StreamCompleteHandler = () => void;

/**
 * Options for streaming agent execution
 */
export interface StreamingOptions {
  onEvent?: StreamEventHandler;
  onError?: StreamErrorHandler;
  onComplete?: StreamCompleteHandler;
  onExecutionStart?: (executionData: { executionId: string; streamId: string }) => void;
  reconnectAttempts?: number;
  reconnectDelay?: number;
}

/**
 * Internal state for SSE connection management
 */
interface SSEConnectionState {
  isConnected: boolean;
  reconnectAttempts: number;
  lastHeartbeat: number;
  abortController: AbortController;
  tokenRefreshTimer?: NodeJS.Timeout;
  heartbeatTimer?: NodeJS.Timeout;
}

/**
 * Execute an agent with real-time streaming updates using hybrid SSE + polling approach
 * 1. POST to start execution (non-blocking), get executionId and sseUrl  
 * 2. Connect to SSE for real-time events
 * 3. Poll status endpoint to know when complete
 */
export async function executeAgentWithStreaming(
  agentName: string,
  request: Omit<AgentStreamingExecutionRequestData, 'agentName'>,
  options: StreamingOptions = {}
): Promise<AbortController> {
  const { onEvent, onError, onComplete, onExecutionStart } = options;
  const abortController = new AbortController();

  try {
    // Step 1: Start execution (non-blocking)
    const startResponse = await authenticatedFetch('/api/agent/execute-stream', {
      method: 'POST',
      body: {
        agentName,
        ...request
      },
      signal: abortController.signal
    });

    const startData = await handleApiResponse<StreamingExecutionStartResponseData>(startResponse);

    const { executionId, sseUrl, statusUrl } = startData;
    
    // Notify that execution has started
    onExecutionStart?.({ executionId, streamId: executionId });

    console.log('🔗 SSE Connection Setup:', {
      executionId,
      sseUrl,
      statusUrl,
      agentName
    });

    // Step 2: Connect to SSE for real-time events with retry logic
    const sseController = new AbortController();
    const maxRetries = options.reconnectAttempts ?? 3;
    const baseDelay = options.reconnectDelay ?? 1000;
    connectToSSE(sseUrl, sseController, onEvent, onError, maxRetries, baseDelay);

    // Step 3: Poll status to know when complete
    const pollController = new AbortController();
    pollExecutionStatus(statusUrl, pollController, (status) => {
      if (status.status === 'completed' || status.status === 'failed') {
        // Stop SSE and polling
        sseController.abort();
        pollController.abort();
        onComplete?.();
      }
    }, onError);

    // Handle abort
    abortController.signal.addEventListener('abort', () => {
      sseController.abort();
      pollController.abort();
    });

  } catch (error) {
    console.error('Execution start error:', error);
    onError?.(error instanceof Error ? error : new Error('Failed to start execution'));
  }

  return abortController;
}

/**
 * Get JWT token expiration time in milliseconds
 */
function getTokenExpirationTime(): number | null {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Failed to parse JWT token:', error);
    return null;
  }
}

/**
 * Check if token will expire soon (within 5 minutes)
 */
function isTokenExpiringSoon(): boolean {
  const expirationTime = getTokenExpirationTime();
  if (!expirationTime) return true;
  
  const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
  return expirationTime < fiveMinutesFromNow;
}

/**
 * Refresh access token proactively
 */
async function refreshTokenIfNeeded(): Promise<boolean> {
  // Check if authentication is disabled in development
  if (isAuthDisabled()) {
    console.log('Authentication disabled - skipping proactive token refresh for SSE');
    return true; // Return true to indicate "success" (no refresh needed)
  }

  if (!isTokenExpiringSoon()) return true;
  
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) return false;

    const result = await response.json();
    if (result.success) {
      localStorage.setItem('accessToken', result.data.accessToken);
      localStorage.setItem('refreshToken', result.data.refreshToken);
      console.log('🔄 Token refreshed proactively for SSE connection');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Failed to refresh token for SSE:', error);
    return false;
  }
}

/**
 * Connect to SSE endpoint with robust retry logic and token management
 */
async function connectToSSE(
  sseUrl: string,
  abortController: AbortController,
  onEvent?: StreamEventHandler,
  onError?: StreamErrorHandler,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<void> {
  const connectionState: SSEConnectionState = {
    isConnected: false,
    reconnectAttempts: 0,
    lastHeartbeat: Date.now(),
    abortController
  };

  // Set up token refresh timer
  const scheduleTokenRefresh = () => {
    if (connectionState.tokenRefreshTimer) {
      clearTimeout(connectionState.tokenRefreshTimer);
    }
    
    const expirationTime = getTokenExpirationTime();
    if (expirationTime) {
      const refreshTime = expirationTime - (5 * 60 * 1000); // 5 minutes before expiry
      const delay = Math.max(0, refreshTime - Date.now());
      
      connectionState.tokenRefreshTimer = setTimeout(async () => {
        if (!abortController.signal.aborted) {
          await refreshTokenIfNeeded();
          scheduleTokenRefresh(); // Schedule next refresh
        }
      }, delay);
    }
  };

  // Set up heartbeat monitoring
  const scheduleHeartbeatCheck = () => {
    if (connectionState.heartbeatTimer) {
      clearTimeout(connectionState.heartbeatTimer);
    }
    
    connectionState.heartbeatTimer = setTimeout(() => {
      const timeSinceLastHeartbeat = Date.now() - connectionState.lastHeartbeat;
      if (timeSinceLastHeartbeat > 60000 && !abortController.signal.aborted) { // 60 seconds
        console.log('⚠️ SSE heartbeat timeout, attempting reconnection');
        attemptConnection();
      }
    }, 65000); // Check every 65 seconds
  };

  const attemptConnection = async (): Promise<void> => {
    if (abortController.signal.aborted) return;
    
    console.log(`📡 Attempting SSE connection to: ${sseUrl} (attempt ${connectionState.reconnectAttempts + 1}/${maxRetries + 1})`);
    
    try {
      // Refresh token if needed before connecting
      await refreshTokenIfNeeded();
      
      const response = await authenticatedFetch(sseUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: HTTP ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body available for SSE');
      }

      console.log('✅ SSE connection established successfully');
      connectionState.isConnected = true;
      connectionState.reconnectAttempts = 0;
      connectionState.lastHeartbeat = Date.now();

      // Start token refresh scheduling
      scheduleTokenRefresh();
      scheduleHeartbeatCheck();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (!abortController.signal.aborted) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('📡 SSE stream ended');
            break;
          }

          // Update heartbeat on any data received
          connectionState.lastHeartbeat = Date.now();

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete SSE messages
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          let currentEvent: { event?: string; data?: string; id?: string } = {};
          
          for (const line of lines) {
            const trimmedLine = line.trim();
            
            if (trimmedLine === '') {
              // Empty line indicates end of event
              if (currentEvent.event && currentEvent.data) {
                try {
                  const data = JSON.parse(currentEvent.data);
                  const streamEvent: AgentExecutionStreamEvent = {
                    type: currentEvent.event as any,
                    data
                  };
                  
                  console.log('📨 SSE event received:', currentEvent.event, data);
                  
                  // Handle heartbeat events
                  if (currentEvent.event === 'heartbeat') {
                    connectionState.lastHeartbeat = Date.now();
                  } else {
                    onEvent?.(streamEvent);
                  }
                } catch (error) {
                  console.error('Error parsing SSE event:', error, currentEvent);
                  onError?.(error instanceof Error ? error : new Error('Failed to parse SSE event'));
                }
              }
              currentEvent = {};
            } else if (trimmedLine.startsWith('event: ')) {
              currentEvent.event = trimmedLine.substring(7);
            } else if (trimmedLine.startsWith('data: ')) {
              currentEvent.data = trimmedLine.substring(6);
            } else if (trimmedLine.startsWith('id: ')) {
              currentEvent.id = trimmedLine.substring(4);
            }
          }
        }
      } finally {
        reader.releaseLock();
        connectionState.isConnected = false;
      }

    } catch (error) {
      connectionState.isConnected = false;
      
      if (abortController.signal.aborted) {
        console.log('🔌 SSE connection aborted (expected)');
        return;
      }

      console.error('❌ SSE connection error:', error);
      
      // Attempt retry with exponential backoff
      if (connectionState.reconnectAttempts < maxRetries) {
        connectionState.reconnectAttempts++;
        const delay = baseDelay * Math.pow(2, connectionState.reconnectAttempts - 1);
        const jitter = Math.random() * 1000; // Add jitter to prevent thundering herd
        
        console.log(`🔄 Retrying SSE connection in ${delay + jitter}ms (attempt ${connectionState.reconnectAttempts}/${maxRetries})`);
        
        setTimeout(() => {
          if (!abortController.signal.aborted) {
            attemptConnection();
          }
        }, delay + jitter);
      } else {
        console.error('❌ Max SSE reconnection attempts reached');
        onError?.(error instanceof Error ? error : new Error('SSE connection failed after retries'));
      }
    }
  };

  // Clean up timers when connection is aborted
  abortController.signal.addEventListener('abort', () => {
    if (connectionState.tokenRefreshTimer) {
      clearTimeout(connectionState.tokenRefreshTimer);
    }
    if (connectionState.heartbeatTimer) {
      clearTimeout(connectionState.heartbeatTimer);
    }
  });

  // Start initial connection attempt
  await attemptConnection();
}

/**
 * Poll execution status to know when complete
 */
function pollExecutionStatus(
  statusUrl: string,
  abortController: AbortController,
  onStatusUpdate: (status: { status: string; result?: any; error?: string }) => void,
  onError?: StreamErrorHandler
): void {
  const poll = async () => {
    try {
      if (abortController.signal.aborted) return;

      const response = await authenticatedFetch(statusUrl, {
        method: 'GET',
        signal: abortController.signal
      });

      const status = await handleApiResponse<{
        status: string;
        result?: any;
        error?: string;
      }>(response);
      
      onStatusUpdate(status);

      // Continue polling if not complete
      if (status.status === 'pending' || status.status === 'running') {
        if (!abortController.signal.aborted) {
          setTimeout(poll, 1000); // Poll every second
        }
      }

    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error('Status polling error:', error);
        onError?.(error instanceof Error ? error : new Error('Status polling failed'));
      }
    }
  };

  // Start polling immediately
  poll();
}

/**
 * Check if a chat session has active executions
 */
export const getSessionExecutionStatus = (sessionId: number) => ({
  queryKey: ['execution-status', sessionId],
  queryFn: createQueryFn<SessionExecutionStatusResponseData>(`/api/agent/execution-status/${sessionId}`),
  staleTime: 0, // Always fetch fresh execution status
  refetchInterval: false, // Don't auto-refetch - we'll control when to check
});

/**
 * Test streaming connection to verify SSE endpoint is working
 * Uses authenticatedFetch following CLIENT_API.md guidelines
 */
export async function testStreamingConnection(): Promise<boolean> {
  try {
    const response = await authenticatedFetch('/api/agent/stream/test', {
      method: 'GET',
      headers: {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      }
    });

    return response.ok && response.headers.get('content-type')?.includes('text/event-stream') === true;
  } catch (error) {
    console.error('Streaming test failed:', error);
    return false;
  }
}
